import { NgClass } from '@angular/common';
import { Component, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TemplateVar } from '@shared/index';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CodemirrorHtmlComponent } from 'src/app/shared/components/ai-bot/utils/codemirror-html/codemirror-html.component';
import { HintComponent } from '@shared-angular/components/hint/hint.component';
import { ManualLinkComponent } from 'src/app/shared/components/rag-admin/manual-link/manual-link.component';
import { AdminService } from '@shared-angular/services/admin.service';
import { LoginService } from '@shared-angular/services/login.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { CodemirrorAiComponent } from "../../../../../../shared/components/ai-bot/utils/codemirror-ai/codemirror-ai.component";

@Component({
  templateUrl: './bot-task-tab.component.html',
  styleUrl: './bot-task-tab.component.scss',
  imports: [NgClass, FormsModule, HintComponent, CodemirrorAiComponent, ManualLinkComponent, CardModule, DialogModule, InputTextModule, SelectModule, TableModule, TextareaModule, ToggleSwitchModule, ButtonModule, CodemirrorHtmlComponent]
})
export class BotTaskTabComponent {

  public botInstruction = $localize`:@@bot-instructions-placeholder:Du bist ein Bot, der ...`;
  public isTemplateVarHtmlEditorVisible = false;
  public templateVarHtmlEditorValue = '';
  public templateVarHtmlEditorIndex: number | null = null;
  public templateVarTypes = [
    { label: 'Text', value: 'string' },
    { label: 'Zahl', value: 'number' },
    { label: 'Boolean', value: 'boolean' }
  ];

  constructor(
    private adminService: AdminService,
    public toastr: ToastrService,
    public loginService: LoginService,
    public rss: RouteShareService
  ) { }

  public async onTemplateToggle(isTemplate: boolean) {
    this.rss.botConfig.isTemplate = isTemplate;
    await this.rss.saveBotConfigField('isTemplate');
    this.rss.botConfig.isTemplateDraft = isTemplate;
    await this.rss.saveBotConfigField('isTemplateDraft');
  }

  public async onTemplateDraftToggle(isTemplateDraft: boolean) {
    this.rss.botConfig.isTemplateDraft = isTemplateDraft;
    await this.rss.saveBotConfigField('isTemplateDraft');
  }

  public addTemplateVar() {
    this.rss.botConfig.templateVars.push({ name: '', description: '', type: 'string', value: '' });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const newTemplateVar = this.rss.botConfig.templateVars[this.rss.botConfig.templateVars.length - 1]!;
    newTemplateVar.isAiVariable = false;
    newTemplateVar.showInWizard = true;
    newTemplateVar.deleteAfterTemplateUsage = false;
    void this.rss.saveBotConfigField('templateVars');
  }

  public removeTemplateVar(index: number) {
    this.rss.botConfig.templateVars.splice(index, 1);
    void this.rss.saveBotConfigField('templateVars');
  }

  public onTemplateVarTypeChange(tv: TemplateVar, index: number) {
    const currentIsAiVariable = tv.type === 'string' ? !!tv.isAiVariable : false;
    const currentShowInWizard = tv.showInWizard !== false;
    if (tv.type === 'number') {
      this.rss.botConfig.templateVars[index] = { name: tv.name, description: tv.description, type: 'number', value: 0 };
      this.rss.botConfig.templateVars[index].isAiVariable = false;
    } else if (tv.type === 'boolean') {
      this.rss.botConfig.templateVars[index] = { name: tv.name, description: tv.description, type: 'boolean', value: false };
      this.rss.botConfig.templateVars[index].isAiVariable = false;
    } else {
      this.rss.botConfig.templateVars[index] = { name: tv.name, description: tv.description, type: 'string', value: '' };
      this.rss.botConfig.templateVars[index].isAiVariable = currentIsAiVariable;
    }
    this.rss.botConfig.templateVars[index].showInWizard = currentShowInWizard;
    void this.rss.saveBotConfigField('templateVars');
  }

  public toggleAiVariable(tv: TemplateVar) {
    if (tv.type !== 'string') {
      return;
    }
    tv.isAiVariable = !(tv.isAiVariable === true);
    void this.rss.saveBotConfigField('templateVars');
  }

  public isAiVariableEnabled(tv: TemplateVar): boolean {
    return tv.type === 'string' && tv.isAiVariable === true;
  }

  public toggleShowInWizard(tv: TemplateVar) {
    tv.showInWizard = !this.isShowInWizardEnabled(tv);
    void this.rss.saveBotConfigField('templateVars');
  }

  public isShowInWizardEnabled(tv: TemplateVar): boolean {
    return tv.showInWizard !== false;
  }

  public toggleDeleteAfterTemplateUsage(tv: TemplateVar) {
    tv.deleteAfterTemplateUsage = !this.isDeleteAfterTemplateUsageEnabled(tv);
    void this.rss.saveBotConfigField('templateVars');
  }

  public isDeleteAfterTemplateUsageEnabled(tv: TemplateVar): boolean {
    return tv.deleteAfterTemplateUsage === true;
  }

  public saveTemplateVars() {
    this.rss.botConfig.templateVars = this.rss.botConfig.templateVars.map(tv => ({
      ...tv,
      name: tv.name.trim(),
      description: tv.description.trim(),
      ...(tv.type === 'string' ? { value: tv.value.trim() } : {})
    })) as typeof this.rss.botConfig.templateVars;
    void this.rss.saveBotConfigField('templateVars');
  }

  public onTemplateVarRowReorder(_event: unknown) {
    this.saveTemplateVars();
  }

  public openTemplateVarHtmlEditor(index: number) {
    const templateVar = this.rss.botConfig.templateVars[index];
    if (!templateVar || templateVar.type !== 'string') return;

    this.templateVarHtmlEditorIndex = index;
    this.templateVarHtmlEditorValue = templateVar.value;
    this.isTemplateVarHtmlEditorVisible = true;
  }

  public onTemplateVarHtmlEditorChange(event: { code: string }) {
    this.templateVarHtmlEditorValue = event.code;
  }

  public saveTemplateVarHtmlEditor() {
    if (this.templateVarHtmlEditorIndex === null) return;

    const templateVar = this.rss.botConfig.templateVars[this.templateVarHtmlEditorIndex];
    if (!templateVar || templateVar.type !== 'string') {
      this.closeTemplateVarHtmlEditor();
      return;
    }

    (templateVar as { value: string }).value = this.templateVarHtmlEditorValue;
    this.saveTemplateVars();
    this.closeTemplateVarHtmlEditor();
  }

  public closeTemplateVarHtmlEditor() {
    this.isTemplateVarHtmlEditorVisible = false;
    this.templateVarHtmlEditorIndex = null;
    this.templateVarHtmlEditorValue = '';
  }


  // Signal-backed, weil im Botly-Callback (window-Funktion) zurueckgesetzt.
  private readonly _isBotlyLoading = signal(false);
  public get isBotlyLoading(): boolean { return this._isBotlyLoading(); }
  public set isBotlyLoading(value: boolean) { this._isBotlyLoading.set(value); }

  public openBotly(): void {
    if (this.isBotlyLoading) return;
    this.isBotlyLoading = true;

    window.promptBotlyReady = () => { this.onBotlyReady(); };

    window.promptFromBotly = (css: string) => { this.applyBotlyCss(css); };

    window.RAGBOT?.setChatbotUrlOnce('https://ki-toolbox.reflactportal.com/de/bot/6a881ac80538c99d84bdae4f/chat');
  }

  private onBotlyReady(): void {
    window.RAGBOT?.openChatbot();
    window.RAGBOT?.blockBackground();
    this.isBotlyLoading = false;
  }

  private applyBotlyCss(css: string): void {
    this.rss.botConfig.botInstruction = css;
    void this.rss.saveBotConfigField('botInstruction');
    window.RAGBOT?.closeChatbot();
  }


}
